import React, { useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Warning, Database, UserCircle, CheckCircle } from '@phosphor-icons/react';

export default function Settings() {
  const { user, updateProfile } = useAuth();

  // Reset data state
  const [confirm, setConfirm] = useState('');
  const [resetting, setResetting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');

  const reset = async () => {
    setResetting(true); setError('');
    try {
      await api.post('/admin/reset-data', { confirm });
      setDone(true);
      setConfirm('');
      setTimeout(() => setDone(false), 5000);
    } catch (e) {
      setError(e.response?.data?.detail || '出错了');
    } finally { setResetting(false); }
  };

  const saveProfile = async () => {
    setProfileErr(''); setProfileMsg(''); setProfileSaving(true);
    try {
      const emailChanged = email.toLowerCase() !== user?.email?.toLowerCase();
      const wantsPwdChange = !!newPwd;
      if (wantsPwdChange && newPwd !== newPwd2) {
        throw { response: { data: { detail: '两次新密码不一致' } } };
      }
      if (wantsPwdChange && newPwd.length < 6) {
        throw { response: { data: { detail: '新密码至少 6 位' } } };
      }
      const payload = {};
      if (name && name !== user?.name) payload.name = name;
      if (emailChanged) payload.email = email;
      if (wantsPwdChange) payload.new_password = newPwd;
      if (emailChanged || wantsPwdChange) {
        if (!currentPwd) throw { response: { data: { detail: '修改邮箱或密码需要输入当前密码' } } };
        payload.current_password = currentPwd;
      }
      if (Object.keys(payload).length === 0) {
        setProfileMsg('没有变更');
        setProfileSaving(false);
        return;
      }
      const result = await updateProfile(payload);
      setProfileMsg(result.changed ? '✓ 已保存!下次登入用新的信息' : '没有变更');
      setCurrentPwd(''); setNewPwd(''); setNewPwd2('');
    } catch (e) {
      setProfileErr(e.response?.data?.detail || '保存失败');
    } finally {
      setProfileSaving(false);
    }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-5 fade-in max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-semibold text-white tracking-tight">系统设置</h1>
        <p className="text-sm text-slate-400 mt-0.5">个人账号 + 高级管理</p>
      </div>

      {/* ============ 我的账号 ============ */}
      <div className="card-base p-6" data-testid="my-profile-section">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
            <UserCircle size={22} weight="duotone" className="text-indigo-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">我的账号</h2>
            <p className="text-sm text-slate-400 mt-0.5">修改自己的姓名、邮箱、密码</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label-tiny block mb-1.5">姓名</label>
            <input
              className="input-base"
              value={name}
              onChange={e => setName(e.target.value)}
              data-testid="profile-name"
            />
          </div>
          <div>
            <label className="label-tiny block mb-1.5">邮箱 (登录用)</label>
            <input
              type="email"
              className="input-base"
              value={email}
              onChange={e => setEmail(e.target.value)}
              data-testid="profile-email"
            />
            {email.toLowerCase() !== user?.email?.toLowerCase() && (
              <div className="text-xs text-amber-300 mt-1.5">⚠️ 修改邮箱需要输入当前密码确认</div>
            )}
          </div>
          <div className="h-px bg-slate-700/50 my-2" />
          <div>
            <label className="label-tiny block mb-1.5">当前密码 (修改邮箱/密码时填)</label>
            <input
              type="password"
              className="input-base"
              value={currentPwd}
              onChange={e => setCurrentPwd(e.target.value)}
              placeholder="不修改邮箱和密码就不用填"
              data-testid="profile-current-password"
              autoComplete="current-password"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-tiny block mb-1.5">新密码</label>
              <input
                type="password"
                className="input-base"
                value={newPwd}
                onChange={e => setNewPwd(e.target.value)}
                placeholder="至少 6 位,可空"
                data-testid="profile-new-password"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="label-tiny block mb-1.5">再输一次新密码</label>
              <input
                type="password"
                className="input-base"
                value={newPwd2}
                onChange={e => setNewPwd2(e.target.value)}
                data-testid="profile-new-password-confirm"
                autoComplete="new-password"
              />
            </div>
          </div>
        </div>

        {profileErr && (
          <div className="mt-3 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg p-3">
            {profileErr}
          </div>
        )}
        {profileMsg && (
          <div className="mt-3 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-center gap-2">
            <CheckCircle size={16} weight="duotone" /> {profileMsg}
          </div>
        )}

        <button
          onClick={saveProfile}
          disabled={profileSaving}
          className="btn-primary w-full mt-4 py-2.5"
          data-testid="save-profile-btn"
        >
          {profileSaving ? '保存中…' : '保存修改'}
        </button>
      </div>

      {/* ============ 重置数据 (admin only) ============ */}
      {isAdmin && (
      <div className="card-base p-6 border-rose-500/30">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-lg bg-rose-500/15 border border-rose-500/30 flex items-center justify-center">
            <Database size={22} weight="duotone" className="text-rose-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">重置测试数据</h2>
            <p className="text-sm text-slate-400 mt-0.5">清空所有订单、客户、银行流水、开销、借支记录</p>
          </div>
        </div>

        <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-4 mb-4 text-sm">
          <div className="flex items-start gap-2">
            <Warning size={18} weight="fill" className="text-rose-400 mt-0.5 flex-shrink-0" />
            <div className="text-rose-200">
              <strong>不可恢复</strong>。建议你在正式启用前使用此功能清空所有测试数据。
              <br/>
              <span className="text-rose-300/80 text-xs">保留:管理员账号、员工账号、银行账户配置、汇率设置</span>
              <br/>
              <span className="text-rose-300/80 text-xs">清空:订单、客户、银行充值流水、对账、开销、借支、工资发放、删除日志</span>
            </div>
          </div>
        </div>

        <label className="label-tiny block mb-2">输入「RESET」确认</label>
        <input
          type="text"
          className="input-base font-num"
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          placeholder="RESET"
          data-testid="reset-confirm"
        />

        {error && <div className="text-sm text-rose-400 mt-2">{error}</div>}

        {done && (
          <div className="mt-3 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
            ✓ 所有测试数据已清空,系统已准备好正式启用
          </div>
        )}

        <button
          onClick={reset}
          disabled={confirm !== 'RESET' || resetting}
          className="btn-danger w-full mt-4 py-3"
          data-testid="reset-data-btn"
        >
          {resetting ? '执行中…' : '⚠️ 重置所有数据'}
        </button>
      </div>
      )}
    </div>
  );
}
